import { MutableRefObject, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  TopElement,
  ManteinanceFilter,
  Installation,
  Masters,
  TimelineData,
  DatePreventives,
  PreventData,
  Incident,
  Site,
  Choropleth,
  IntelligenceActive,
  FiltersIntel,
  LoginRequest,
  UserLogin,
  UserLoginWithSecret,
  MapPoi
} from './../types.d'
import type {
  Activity,
  ActivityFilter,
  Callback,
  Event,
  EventsAndSystems,
  KPI,
  Pagination,
  Request,
  RequestsAndSystems,
  System,
  User,
  UserInstallation,
  UserSystem,
  Manteinance
} from 'src/types.js'
import {
  useFetch,
  useFetchCb,
  usePaginatedFetch,
  PaginatedData,
  useFetchUncachedCb
} from '@gluedigital/ruse-fetch-extras'
import { login, logout } from 'src/store/actions/user'
import { useZoom } from './useMap'
import { ClimateThreat } from 'src/utils/fire'
//https://apisecurity.securitas.es/

const base = (process.env.API_HOST || 'http://localhost:8080/') + 'api'

export const useUserActions = () => {
  const doFetch = useFetchUncachedCb()
  const dispatch = useDispatch()

  return {
    login: async ({
      associationRequest,
      code,
      email,
      password
    }: LoginRequest): Promise<UserLogin> => {
      const payload = await doFetch<UserLogin>(`${base}/auth/login`, {
        body: {
          email,
          password,
          code,
          associationRequest
        }
      })
      dispatch(login(payload))
      return payload
    },
    associate: async (
      secret: string,
      id: string
    ): Promise<UserLoginWithSecret> => {
      const payload = await doFetch<UserLoginWithSecret>(
        `${base}/auth/validate/mfa`,
        {
          body: {
            secret,
            id
          }
        }
      )
      const { secret: secretResponse, ...user } = payload
      dispatch(login(user))
      return payload
    },
    logout: () => dispatch(logout())
  }
}

export const useUser = (uid: number | 'me'): User =>
  useFetch(uid && `${base}/user/${uid}`)

export const useSections = (systemId: number): User =>
  useFetch(systemId && `${base}/installation/${systemId}/zones`)

export const useUserInstallations = (
  uid: number | string
): UserInstallation[] => useFetch(uid && `${base}/user/${uid}/installations`)

export const useKpis = (numDays: number, systemId?: number): KPI[] =>
  useFetch(numDays && base + '/kpis', { params: { numDays, systemId } })

export const useChangePassword = (uid: number): Callback<{}> =>
  useFetchCb(`${base}/user/recovery/${uid}`)

export const useUserEnabled = (uid: number, enabled: boolean): Callback<User> =>
  useFetchCb(`${base}/user/${uid}/status`, { method: 'PUT', body: { enabled } })

export const useSystem = (systemIds: number | string): UserSystem[] =>
  // Note that you can actually request any number of ids by using a comma-separated string
  useFetch(systemIds && `${base}/installation/${systemIds}`)

export const usePagSystemEvents = (
  sid: number,
  filters: ActivityFilter
): PaginatedData<Event[]> =>
  usePaginatedFetch(`${base}/installation/${sid}/events`, {
    params: { ...filters },
    hasNext: (d) => d.length === 8
  })

export const usePagSystemRequests = (
  sid: number,
  filters: ActivityFilter
): PaginatedData<Request[]> =>
  usePaginatedFetch(`${base}/installation/${sid}/requests`, {
    params: { ...filters },
    hasNext: (d) => d.length === 8
  })

export const useInstallationInfo = (installationId?: string): Installation =>
  useFetch(
    installationId
      ? `${base}/sat/installation/${installationId}/info`
      : undefined
  )

export const useManteinances = (
  type: string,
  installationId?: number,
  filters?: ManteinanceFilter
): Manteinance[] =>
  useFetch(
    installationId
      ? `${base}/sat/${type}/installation/${installationId}`
      : `${base}/sat/${type}`,
    {
      params: filters
    }
  )

export const usePagManteinances = (
  type: string,
  installationId?: number,
  filters?: ManteinanceFilter
): PaginatedData<Manteinance[]> =>
  usePaginatedFetch(
    installationId
      ? `${base}/sat/${type}/installation/${installationId}`
      : `${base}/sat/${type}`,
    {
      params: { ...filters },
      hasNext: (d) => d.length === 20
    }
  )

export const useMasters = (): Masters => useFetch(base + '/sat/masters')

export const useGraphData = (filter: string): PreventData =>
  useFetch(base + '/sat/daily', {
    params: {
      filter: filter === 'SEL' ? 'SEG' : filter // They are called diferently in each table
    }
  })

export const useManteinance = (manteinanceId: number): Manteinance =>
  useFetch(`${base}/sat/jobs/${manteinanceId}`)

export const useTopLocations = (filters?: any): TopElement[] =>
  useFetch(base + '/sat/top/locations', { params: filters })

export const useTopCauses = (
  filters?: any,
  installationId?: number
): TopElement[] =>
  useFetch(
    base + '/sat/top/causes' + (installationId ? '/' + installationId : ''),
    { params: filters }
  )

export const useInstallationsManteinances = (): Installation[] =>
  useFetch(base + '/sat/installations')

export const useInstallationManteinances = (
  id: number,
  filters: ManteinanceFilter
): TimelineData =>
  useFetch(`${base}/sat/installation/${id}`, {
    params: {
      ...filters,
      filter: filters.filter === 'SEL' ? 'SEG' : filters.filter // They are called diferently in each table
    }
  })

export const useDatePreventives = (date: string): DatePreventives =>
  useFetch(`${base}/sat/daily/${date}`)

export const useSatIssue = (
  installationId: number,
  symptom: string,
  subSymptom: string,
  comment: string
): Callback<any> =>
  useFetchCb(`${base}/sat/issue`, {
    method: 'POST',
    body: {
      installationId,
      symptom,
      subSymptom,
      comment
    }
  })

export const useAssistance = (
  name: string,
  email: string,
  phone: string
): Callback<any> =>
  useFetchCb(`${base}/sat/call-me-back`, {
    method: 'POST',
    body: { name, email, phone }
  })

export const usePagMyEvents = (
  filters: ActivityFilter
): PaginatedData<EventsAndSystems> =>
  usePaginatedFetch(base + '/installation/events', {
    params: { ...filters },
    merger: (a, b) => ({
      events: [...a.events, ...b.events],
      systems: [...a.systems, ...b.systems]
    }),
    hasNext: (d) => d.events.length === 10
  })

export const usePagMyRequests = (
  filters: ActivityFilter
): PaginatedData<RequestsAndSystems> =>
  usePaginatedFetch(base + '/installation/requests', {
    params: { ...filters },
    merger: (a, b) => ({
      requests: [...a.requests, ...b.requests],
      systems: [...a.systems, ...b.systems]
    }),
    hasNext: (d) => d.requests.length === 10
  })

export const useMySystems = (pagination: Pagination): System[] =>
  useFetch(base + '/user/systems', { params: pagination })

export const useLastActivity = (): Activity =>
  useFetch(base + '/user/me/activity')

export const useUsers = (): User[] => useFetch(base + '/user/users')

// Not really hooks, but useful nonetheless
const autoArmTypes = [
  // RSI
  '429',
  '429SD',
  // SPC
  'SPC',
  'B87',
  'T87',
  '364',
  'FLEXC'
  // Add other systems here as they get implemented
]

export const canAutoArm = (system: System): boolean => {
  return autoArmTypes.indexOf(system.sistema_tipo.trim()) !== -1
}

export const canAutoArmAny = (systems: System[]): boolean => {
  return (
    systems.length > 0 &&
    systems.some((s) => autoArmTypes.indexOf(s.sistema_tipo.trim()) !== -1)
  )
}

export const useIncidents = (filters?: FiltersIntel): Incident[] =>
  useFetch(base + '/incidents', { params: filters })

export const useIncident = (id: string | number): Incident =>
  useFetch(base + '/incidents/' + id)

export const useActives = (): IntelligenceActive[] =>
  useFetch(base + '/actives')

export const useChoropleths = (): Choropleth[] =>
  useFetch(base + '/risk/by/zipCode')

export const useSitesEvents = (
  siteId: number | string,
  params?: any
): { site: Site; events: any[] } =>
  useFetch(base + '/risk/sites/' + +siteId + '/events', { params })

export type RiskMode = 'descriptive' | 'risks' | 'multi'

export const useAllSitesCounts = (mode: RiskMode, params?: any): Site[] => {
  const urls = {
    descriptive: '/risk/sites/counts',
    risks: '/risk/sites',
    multi: '/risk/multi/sites'
  }
  return useFetch(base + urls[mode], { params })
}

export const useChartStatusData = (): any[] =>
  useFetch(base + '/risk/sites/analysis/status')

export const useChartActionData = (): any[] =>
  useFetch(base + '/risk/sites/analysis/action')

export const useSiteRisksData = (siteId: number | string): Site =>
  useFetch(base + '/risk/risks/' + +siteId)

export const useMapData = (
  theMap: MutableRefObject<any>,
  noCS?: boolean,
  multi?: boolean
) => {
  const zoom = useZoom(theMap)
  const [data, setData] = useState({ mun: null, zip: null, cs: null })
  const myFetch = useFetchUncachedCb()
  const url = base + (multi ? '/risk/multi/by/' : '/risk/by/')

  useEffect(() => {
    if (zoom <= 9) {
      if (!data.mun) {
        myFetch(url + 'municipality').then((mun) =>
          setData((x) => ({ ...x, mun }))
        )
      }
    } else if (zoom <= 12 || noCS) {
      if (!data.zip) {
        myFetch(url + 'zipCode').then((zip) => setData((x) => ({ ...x, zip })))
      }
    } else if (!noCS) {
      if (!data.cs) {
        myFetch(url + 'censusSection').then((cs) =>
          setData((x) => ({ ...x, cs }))
        )
      }
    }
  }, [zoom])

  return data
}

export const useMobileMapping = (zoom: number): { [k: string]: number } => {
  const [zoomMappings, setZoomMappings] = useState({})
  const myFetch = useFetchUncachedCb()

  useEffect(() => {
    if (!zoomMappings[zoom]) {
      setZoomMappings((z) => ({ ...z, [zoom]: {} }))
      const url = `${base}/risk/multi/mobile/layers/${zoom}`
      myFetch(url).then((data) =>
        setZoomMappings((z) => ({ ...z, [zoom]: data }))
      )
    }
  }, [zoom])

  return zoomMappings[zoom] || {}
}

interface MultiRiskAverage {
  past: number
  present: number
  future: number
}

export const useMultiRiskAverage = (): MultiRiskAverage => {
  const bounds = useSelector((s) => s.mapBounds)
  const by = bounds?.zoom <= 9 ? 'municipality' : 'zipCode'
  const url = bounds.north ? `${base}/risk/multi/by/${by}/average` : null
  return useFetch<MultiRiskAverage>(url, {
    params: {
      n: bounds.north,
      s: bounds.south,
      e: bounds.east,
      w: bounds.west
    }
  })
}

export const useFireRiskData = () => {
  const [data, setData] = useState<{ id: number; risk: number }[]>([])
  const fetch = useFetchUncachedCb()
  fetch(`${base}/risk/fire/aemet`).then((res) =>
    setData(res as { id: number; risk: number }[])
  )
  return data
}

export const useMultiCrimeData = (theMap: MutableRefObject<any>) => {
  const [zoom, setZoom] = useState(
    theMap.current ? theMap.current.getZoom() : 0
  )
  const [data, setData] = useState({ pro: null, mun: null, zip: null })
  const myFetch = useFetchUncachedCb()
  const url = base + '/risk/multi/crime/by/'

  useEffect(() => {
    const handler = () => setZoom(theMap.current.getZoom())
    setTimeout(() => theMap.current.on('zoomend', handler), 0)
    handler()
  }, [])

  useEffect(() => {
    if (zoom < 9) {
      if (!data.pro) {
        myFetch(url + 'province').then((pro) => setData((x) => ({ ...x, pro })))
      }
    } else {
      if (!data.mun) {
        myFetch(url + 'municipality').then((mun) =>
          setData((x) => ({ ...x, mun }))
        )
      }
    }
  }, [zoom])

  return data
}

export interface MultiCrimeAverage {
  variacion: number
  rlesiones: number
  rviolencia: number
  ractivos: number
  rhurtos: number
  rvehiculos: number
  rdrogas: number
  td: number
}

export interface MultiCrimeStats extends MultiCrimeAverage {
  nombre: string
}

export const useMultiCrimeAverage = (): MultiCrimeAverage => {
  const bounds = useSelector((s) => s.mapBounds)
  const url = bounds.north
    ? `${base}/risk/multi/crime/by/municipality/average`
    : null
  return useFetch<MultiCrimeAverage>(url, {
    params: {
      n: bounds.north,
      s: bounds.south,
      e: bounds.east,
      w: bounds.west
    }
  })
}

export const useMultiCrimeGlobalAverage = (): MultiCrimeAverage => {
  const url = `${base}/risk/multi/crime/by/municipality/average`
  return useFetch<MultiCrimeAverage>(url, {
    params: { n: 43, s: 27, e: 5, w: -18 }
  })
}

export const useMultiCrimeMun = (mun: number): MultiCrimeStats =>
  useFetch<MultiCrimeStats>(`${base}/risk/multi/crime/by/municipality/${mun}`)

export const useFireMapping = (zoom: number): { [k: string]: number } => {
  const [zoomMappings, setZoomMappings] = useState({})
  const myFetch = useFetchUncachedCb()

  useEffect(() => {
    if (!zoomMappings[zoom]) {
      setZoomMappings((z) => ({ ...z, [zoom]: {} }))
      const url = `${base}/risk/multi/fire/layers/${zoom}`
      myFetch(url).then((data) =>
        setZoomMappings((z) => ({ ...z, [zoom]: data }))
      )
    }
  }, [zoom])

  return zoomMappings[zoom] || {}
}

export const useFireStations = (): MapPoi[] =>
  useFetch(base + '/risk/multi/fire/stations')

export const useClimateThreat = (lat: number, lon: number) =>
  useFetch<ClimateThreat>(`${base}/risk/fire`, {
    params: {
      lat,
      lon
    }
  })
